import { useCallback, useId, useRef, useState } from "react";
import { Upload, X, FileImage, Loader2 } from "lucide-react";
import { cn } from "@/lib/cn";

export interface UploadedFile {
  id: string;
  name: string;
  size: number;
  type: string;
  previewUrl?: string;
  progress?: number;
  error?: string;
}

export interface FileUploadProps {
  value?: UploadedFile[];
  onChange?: (files: UploadedFile[]) => void;
  onUpload?: (file: File) => Promise<UploadedFile>;
  label?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  multiple?: boolean;
  accept?: string;
  maxSizeMB?: number;
  id?: string;
  className?: string;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function FileUpload({
  value = [],
  onChange,
  onUpload,
  label,
  error,
  required,
  disabled,
  multiple = true,
  accept = "image/*",
  maxSizeMB = 10,
  id: idProp,
  className,
}: FileUploadProps) {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);

  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  const processFiles = useCallback(
    async (fileList: FileList | File[]) => {
      const files = Array.from(fileList);
      const newFiles: UploadedFile[] = [];

      setUploading(true);
      for (const file of files) {
        if (file.size > maxSizeBytes) {
          newFiles.push({
            id: crypto.randomUUID(),
            name: file.name,
            size: file.size,
            type: file.type,
            error: `File exceeds ${maxSizeMB}MB limit`,
          });
          continue;
        }

        if (onUpload) {
          try {
            const uploaded = await onUpload(file);
            newFiles.push(uploaded);
          } catch {
            newFiles.push({
              id: crypto.randomUUID(),
              name: file.name,
              size: file.size,
              type: file.type,
              error: "Upload failed",
            });
          }
        } else {
          const previewUrl = file.type.startsWith("image/")
            ? URL.createObjectURL(file)
            : undefined;
          newFiles.push({
            id: crypto.randomUUID(),
            name: file.name,
            size: file.size,
            type: file.type,
            previewUrl,
          });
        }
      }
      setUploading(false);

      onChange?.(multiple ? [...value, ...newFiles] : newFiles.slice(0, 1));
    },
    [maxSizeBytes, maxSizeMB, multiple, onChange, onUpload, value],
  );

  function handleRemove(fileId: string) {
    const file = value.find((f) => f.id === fileId);
    if (file?.previewUrl) URL.revokeObjectURL(file.previewUrl);
    onChange?.(value.filter((f) => f.id !== fileId));
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
    if (disabled || uploading) return;
    void processFiles(e.dataTransfer.files);
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    if (!disabled && !uploading) setIsDragging(true);
  }

  function handleDragLeave(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files) void processFiles(e.target.files);
    e.target.value = "";
  }

  return (
    <div className={cn("space-y-1.5", className)}>
      {label && (
        <label className="text-sm font-medium leading-none text-foreground">
          {label}
          {required && <span className="ml-0.5 text-destructive">*</span>}
        </label>
      )}

      <div
        id={id}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => !disabled && !uploading && inputRef.current?.click()}
        className={cn(
          "flex min-h-[120px] cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 text-center transition-colors",
          isDragging
            ? "border-primary bg-primary/5"
            : "border-input hover:border-primary/50 hover:bg-accent/50",
          (disabled || uploading) && "cursor-not-allowed opacity-50",
          error && "border-destructive",
        )}
      >
        {uploading ? (
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        ) : (
          <Upload className="h-8 w-8 text-muted-foreground" />
        )}
        <div>
          <p className="text-sm font-medium text-foreground">
            {uploading ? "Uploading..." : "Drop files here or click to browse"}
          </p>
          <p className="text-xs text-muted-foreground">
            {accept === "image/*" ? "Images" : accept} up to {maxSizeMB}MB
            {multiple ? "" : " (single file)"}
          </p>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleInputChange}
          disabled={disabled || uploading}
          className="sr-only"
        />
      </div>

      {value.length > 0 && (
        <div className="space-y-2">
          {value.map((file) => (
            <div
              key={file.id}
              className={cn(
                "flex items-center gap-3 rounded-md border p-2",
                file.error && "border-destructive bg-destructive/5",
              )}
            >
              {file.previewUrl ? (
                <img
                  src={file.previewUrl}
                  alt={file.name}
                  className="h-10 w-10 rounded object-cover"
                />
              ) : (
                <FileImage className="h-10 w-10 text-muted-foreground" />
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{file.name}</p>
                <p className="text-xs text-muted-foreground">
                  {formatFileSize(file.size)}
                  {file.error && (
                    <span className="ml-2 text-destructive">{file.error}</span>
                  )}
                </p>
                {file.progress !== undefined && file.progress < 100 && (
                  <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-secondary">
                    <div
                      className="h-full rounded-full bg-primary transition-all"
                      style={{ width: `${file.progress}%` }}
                    />
                  </div>
                )}
              </div>
              {!disabled && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(file.id);
                  }}
                  className="shrink-0 rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
